import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Link, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSearchParams } from 'react-router-dom';
import DiscoverEarnings from './DiscoverEarnings';
import DiscoverWheel from './DiscoverWheel';
import EarningsGuide from '../shared/EarningsGuide';
import WheelGuide from '../shared/WheelGuide';
import { earningsService } from '../../services/earningsService';
import { EarningsDiscoveryRow } from '../../types/earnings';

type Segment = 'earnings' | 'wheel';

const Discover: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const explicitSegment = searchParams.get('segment');
  const [resolvedDefault, setResolvedDefault] = useState<Segment | null>(
    explicitSegment === 'wheel' || explicitSegment === 'earnings' ? explicitSegment : null
  );
  const segment: Segment | null = explicitSegment === 'wheel' ? 'wheel'
    : explicitSegment === 'earnings' ? 'earnings'
    : resolvedDefault;
  const setSegment = (v: Segment) => setSearchParams({ segment: v }, { replace: true });
  const [earningsGuideOpen, setEarningsGuideOpen] = useState(false);
  const [wheelGuideOpen, setWheelGuideOpen] = useState(false);

  // Cache earnings list from the auto-detect call so DiscoverEarnings doesn't re-fetch
  const prefetchedEarningsRef = useRef<EarningsDiscoveryRow[] | null>(null);

  // Auto-detect default: check for upcoming earnings recommendations, then pick segment once.
  useEffect(() => {
    if (explicitSegment) return;
    earningsService.getDiscoveryList()
      .then((rows) => {
        prefetchedEarningsRef.current = rows;
        const hasRecommendations = rows.some((r) => r.grade && r.grade !== 'S');
        setResolvedDefault(hasRecommendations ? 'earnings' : 'wheel');
      })
      .catch(() => setResolvedDefault('wheel'));
  }, [explicitSegment]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>Pryvo</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Pry into volatility. Find your edge.
        </Typography>
        <ToggleButtonGroup
          value={segment}
          exclusive
          onChange={(_, v) => v && setSegment(v)}
          size="large"
        >
          <ToggleButton
            value="earnings"
            sx={{ px: 4, py: 1.5, fontSize: '1rem', fontWeight: 600, gap: 1 }}
          >
            Earnings
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ opacity: 0.6, display: { xs: 'none', sm: 'inline-flex' } }}
              onClick={(e) => { e.stopPropagation(); setEarningsGuideOpen(true); }}
            />
          </ToggleButton>
          <ToggleButton
            value="wheel"
            sx={{ px: 4, py: 1.5, fontSize: '1rem', fontWeight: 600, gap: 1 }}
          >
            Wheel
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ opacity: 0.6, display: { xs: 'none', sm: 'inline-flex' } }}
              onClick={(e) => { e.stopPropagation(); setWheelGuideOpen(true); }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ mt: 1.5, display: { xs: 'block', sm: 'none' } }}>
          <Link
            component="button"
            variant="body2"
            underline="always"
            onClick={() => segment === 'earnings' ? setEarningsGuideOpen(true) : setWheelGuideOpen(true)}
          >
            How {segment === 'earnings' ? 'earnings plays' : 'the wheel'} work
          </Link>
        </Box>
      </Box>

      {segment === null
        ? <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        : segment === 'earnings'
          ? <DiscoverEarnings prefetchedRows={prefetchedEarningsRef.current} />
          : <DiscoverWheel />
      }

      <EarningsGuide open={earningsGuideOpen} onClose={() => setEarningsGuideOpen(false)} />
      <WheelGuide open={wheelGuideOpen} onClose={() => setWheelGuideOpen(false)} />
    </Box>
  );
};

export default Discover;
